import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import { useAppStore } from '@/stores/app'

/*
 * WHO MAY OPEN WHICH PAGE (docs/API.md → Permissions).
 *
 * A route says what it needs in its meta: `requiresProvider` (Stranke, all users),
 * `requiresAdmin` (admin rights in the tenant: a tenant admin or a provider admin) and `tenant`
 * (it lives under `/t/:tid/…`). The answers need GET /me, which arrives after a cold load: before
 * that nothing can be told and the checks say yes rather than guess — the router and App.vue
 * ask again once /me is in.
 *
 * `accessRedirect` answers where to go INSTEAD, or null when the page may open:
 *   - a page this person may not use → the landing;
 *   - a tenant user on the address of a tenant they are not a member of → the same page in
 *     their first tenant (the API would answer 404 for every call — docs/API.md: a tenant the
 *     caller is not a member of is 404 code 3);
 *   - an admin page of a tenant where the person is only an editor → that tenant's Pregled
 *     (they may be an admin elsewhere — the rights are per tenant).
 */
export const accessRedirect = (to: RouteLocationNormalized): RouteLocationRaw | null => {
    const appStore = useAppStore()

    if (!appStore.userData || to.meta.requiresAuth === false) return null

    if (to.meta.requiresProvider && !appStore.isProviderAdmin()) return landingRoute()

    const tid = typeof to.params.tid === 'string' ? to.params.tid : ''

    if (to.meta.tenant && tid && !appStore.canOpenTenant(tid)) {
        const first = appStore.firstTenantId()

        if (!first || !to.name) return landingRoute()

        return { name: to.name, params: { ...to.params, tid: first }, query: to.query }
    }

    /*
     * A closed role works its own pages only (docs/API.md §13.2, §13.6): the kitchen the
     * room-service orders and the menu, housekeeping its requests — each lands on the inbox.
     */
    const closed = tid ? appStore.closedRole(tid) : null

    if (to.meta.tenant && closed && !(to.meta.closed ?? []).includes(closed)) {
        return { name: 'requests', params: { tid } }
    }

    // A technician works the field view, nothing else (docs/API.md §16) — but Naprave › Namestitev (docs/PROVISIONING.md).
    const tab = typeof to.params.tab === 'string' ? to.params.tab : ''

    if (to.meta.tenant && tid && appStore.isTechnician(tid) && !to.meta.field && !(to.meta.technicianTabs ?? []).includes(tab)) {
        return { name: 'field', params: { tid } }
    }

    if (to.meta.requiresAdmin && !appStore.isAdmin(tid)) {
        return to.meta.tenant && tid ? { name: 'overview', params: { tid } } : landingRoute()
    }

    return null
}

/*
 * THE LANDING PAGE is a route of its own (`views/Landing.vue`): where a person starts depends on
 * who they are and, for a provider admin, on which tenants exist — so it is decided there, once
 * /me (and the tenant list) are in, not guessed here.
 */
export const landingRoute = (): RouteLocationRaw => ({ name: 'landing' })
